export interface ClientCardProps {
  name: string;
  company?: string | null;
  /** Resolved image URL; falls back to initials when omitted. */
  imageSrc?: string | null;
  /** Preformatted rate string (e.g. "$150.00/hr"). */
  rate?: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  /** Show an edit button in the image overlay. */
  onEdit?: () => void;
  /** Show a delete button in the image overlay. */
  onDelete?: () => void;
  className?: string;
}

/**
 * A contact/client card in the Limitless `user_pages_cards` style: a cover image (or initials
 * fallback) with hover-style action buttons, then name, company, rate and quick-contact links.
 */
export function ClientCard({
  name,
  company,
  imageSrc,
  rate,
  email,
  phone,
  website,
  onEdit,
  onDelete,
  className = '',
}: ClientCardProps) {
  const initials = name.slice(0, 2).toUpperCase();
  return (
    <div className={`card h-100 ${className}`.trim()}>
      <div className="card-img-actions position-relative">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={name}
            className="card-img-top"
            style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover' }}
          />
        ) : (
          <div
            className="card-img-top d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary"
            style={{ width: '100%', aspectRatio: '1 / 1', fontSize: '3rem', fontWeight: 600 }}
          >
            {initials}
          </div>
        )}
        {(onEdit || onDelete) && (
          <div className="position-absolute top-0 end-0 m-2 d-flex gap-1">
            {onEdit && (
              <button type="button" className="btn btn-dark btn-icon btn-sm rounded-pill" title="Edit" onClick={onEdit}>
                <i className="ph-pencil" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="btn btn-dark btn-icon btn-sm rounded-pill text-danger"
                title="Delete"
                onClick={onDelete}
              >
                <i className="ph-trash" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="card-body text-center">
        <h6 className="mb-0">{name}</h6>
        <span className="text-muted">{company || '—'}</span>
        {rate && <div className="mt-2 fw-semibold">{rate}</div>}
        {(email || phone || website) && (
          <div className="d-flex justify-content-center gap-3 mt-3 text-muted">
            {email && (
              <a href={`mailto:${email}`} title={email} className="text-reset">
                <i className="ph-envelope-simple" />
              </a>
            )}
            {phone && (
              <a href={`tel:${phone}`} title={phone} className="text-reset">
                <i className="ph-phone" />
              </a>
            )}
            {website && (
              <a href={website} target="_blank" rel="noreferrer" title={website} className="text-reset">
                <i className="ph-globe" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
